import { useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { createMutationAttempt } from './mutation-attempt';
import { getSession } from '../auth/session-store';
import type { SuccessLine } from './feedback';

function sessionIdentity() {
  const session = getSession();
  return session ? `${session.businessUserId}:${session.businessId}:${session.expiresAt}` : 'anonymous';
}

export function useMutationAttempt<Input, Output>(options: {
  mutationFn(input: Input, key: string): Promise<Output>;
  cloneInput?(input: Input): Input;
  getIdentity?(): string;
  onSuccess?(data: Output, input: Input): void | Promise<void>;
  /** The toast when it lands (lib/feedback.ts); none when the result shows in place. */
  exito?: SuccessLine;
}) {
  const attempt = useRef(createMutationAttempt<Input>(options.cloneInput)).current;
  const getIdentity = options.getIdentity ?? sessionIdentity;
  const identity = useRef(getIdentity()).current;
  const mutation = useMutation({
    meta: options.exito ? { exito: options.exito } : undefined,
    mutationFn: (input: Input) => {
      if (getIdentity() !== identity) throw new Error('La sesión cambió. Abre la página de nuevo antes de guardar.');
      const original = attempt.begin(input);
      return options.mutationFn(original.input, original.key);
    },
    onSuccess: async (data: Output) => {
      const original = attempt.pending();
      attempt.complete();
      if (original && getIdentity() === identity) await options.onSuccess?.(data, original.input);
    },
    onError: (error: unknown) => attempt.fail(error),
  });
  return {
    ...mutation,
    uncertain: attempt.isUncertain(),
    retry: () => {
      const original = attempt.pending();
      if (original && !mutation.isPending) mutation.mutate(original.input);
    },
  };
}
